import {
  getDatabase,
  ref,
  query,
  orderByChild,
  startAt,
  endAt,
  onValue,
  update,
  remove,
  push,
  get,
  set,
} from "firebase/database";
import { Invoice, Team, User, InvoiceStatus } from "../types";
import { auth } from "../firebase";

const database = getDatabase();

// Current factor cache and subscription
let currentFactorCache = 1.6568; // default fallback
let factorInitialized = false;

const initCurrentFactorSubscription = () => {
  if (factorInitialized) return;
  factorInitialized = true;
  // Listen to potential paths to be robust: defaults/currentFactor, details/currentFactor, and detauls/currentFactor (typo in some DBs)
  const defaultsRef = ref(database, "defaults/currentFactor");
  const detailsRef = ref(database, "details/currentFactor");
  const detaulsRef = ref(database, "detauls/currentFactor");

  const handleSnapshot = (snapshot: any) => {
    if (snapshot && snapshot.exists()) {
      const val = snapshot.val();
      const parsed = parseFloat(String(val));
      if (!isNaN(parsed) && parsed > 0) {
        currentFactorCache = parsed;
      }
    }
  };

  try {
    onValue(defaultsRef, handleSnapshot);
  } catch {}
  try {
    onValue(detailsRef, handleSnapshot);
  } catch {}
  try {
    onValue(detaulsRef, handleSnapshot);
  } catch {}
};

// Initialize subscription on module load
initCurrentFactorSubscription();

export const getCurrentFactorValue = (): number => currentFactorCache;

export const getUserData = async (uid: string): Promise<User> => {
  // Try users table first (for admins and supervisors)
  const userRef = ref(database, `users/${uid}`);
  const snapshot = await get(userRef);

  if (snapshot.exists()) {
    const userData = snapshot.val();
    return {
      uid,
      name: userData.name,
      email: userData.email || auth.currentUser?.email || "",
      type: userData.type,
      team: userData.team,
      supervision: userData.supervision ? userData.supervision.split(",") : [],
    };
  }

  // Fallback to users-mobile table
  const mobileUserRef = ref(database, `users-mobile/${uid}`);
  const mobileSnapshot = await get(mobileUserRef);

  if (mobileSnapshot.exists()) {
    const userData = mobileSnapshot.val();
    return {
      uid,
      name: userData.name,
      email: userData.email || auth.currentUser?.email || "",
      type: userData.type,
      team: userData.team,
      supervision: userData.supervision ? userData.supervision.split(",") : [],
    };
  }

  throw new Error("User data not found");
};

export const getUserTeams = async (uid: string): Promise<Team[]> => {
  try {
    const userData = await getUserData(uid);
    const teams: Team[] = [];

    if (userData.type === "admin" || userData.type === "func") {
      const allTeams = [
        "ORCDISTBA01",
        "ORCDISTBA02",
        "ORCDISTBA03",
        "ORCDISTBA04",
        "ORCDISTBA05",
        "ORCDISTBA06",
        "ORCDISTBA07",
        "ORCDISTBA08",
        "ORCDISTBA09",
        "ORCDISTBA10",
        "ORCDISTBA11",
        "ORCDISTBA12",
        "ORCDISTBA13",
        "ORCDISTBA14",
        "ORCDISTBA15",
        "ORCDISTBA16",
        "ORCDISTBA17",
      ];

      allTeams.forEach((teamId) => {
        teams.push({ id: teamId, name: teamId });
      });
    } else if (userData.type === "superv" && userData.supervision) {
      userData.supervision.forEach((teamId) => {
        teams.push({ id: teamId, name: teamId });
      });
    } else if (userData.type === "team") {
      teams.push({ id: userData.team, name: userData.team });
    }

    return teams;
  } catch (error) {
    console.error("Error getting user teams:", error);
    return [];
  }
};

export const getInvoices = async (
  teamId: string,
  startDate: string,
  endDate: string,
): Promise<Invoice[]> => {
  return new Promise((resolve, reject) => {
    const startTimestamp = dateToTimestamp(startDate, "00:00:00");
    const endTimestamp = dateToTimestamp(endDate, "23:59:59");

    const formsRef = ref(database, `forms/${teamId}`);
    const formsQuery = query(
      formsRef,
      orderByChild("date"),
      startAt(startTimestamp),
      endAt(endTimestamp),
    );

    onValue(
      formsQuery,
      (snapshot) => {
        const invoices: Invoice[] = [];

        snapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key!;
          const data = childSnapshot.val();

          // Debug: Log Firebase data for first few invoices
          if (invoices.length < 2) {
            console.log(`Firebase data for invoice ${key}:`, data);
            console.log(`Photos in Firebase:`, data.photos);
            console.log(`Materials in Firebase:`, data.materials);
          }

          if (data.sent_status === "true_opened") {
            const servicesArr = Array.isArray(data.services)
              ? data.services
              : [];
            const computedTotal =
              servicesArr.length > 0
                ? calculateServicesTotal(servicesArr, false)
                : 0;
            const computedTotalWithFactor =
              servicesArr.length > 0
                ? calculateServicesTotal(servicesArr, true)
                : 0;
            // Build invoice ensuring header fields take precedence over any root-level duplicates
            const invoice: Invoice = {
              ...data, // bring raw data first
              key,
              id: data.id || key,
              project: data.header?.project || data.project || "",
              title: data.header?.title || data.title || "",
              pms: data.header?.pms || data.pms || "",
              os: data.header?.os || data.os || "",
              city: data.header?.city || data.city || "",
              date: data.header?.date || data.date || "",
              total: computedTotal,
              totalWithFactor: computedTotalWithFactor,
              status: getInvoiceStatus(data),
              checked: data.checked || false,
              viewedByFat: data.viewedByFat || false,
              paid: data.paid || false,
              attached: data.attached || data.archived || false,
              team: teamId,
              header: data.header || {},
              services: data.services || [],
              photos: data.photos || [],
              materials: data.materials || [],
            };

            invoices.push(invoice);
          }
        });

        resolve(invoices.reverse());
      },
      (error) => {
        reject(error);
      },
    );
  });
};

export const subscribeToInvoices = (
  teamId: string,
  startDate: string,
  endDate: string,
  callback: (invoices: Invoice[]) => void,
): (() => void) => {
  const startTimestamp = dateToTimestamp(startDate, "00:00:00");
  const endTimestamp = dateToTimestamp(endDate, "23:59:59");

  const formsRef = ref(database, `forms/${teamId}`);
  const formsQuery = query(
    formsRef,
    orderByChild("date"),
    startAt(startTimestamp),
    endAt(endTimestamp),
  );

  const unsubscribe = onValue(
    formsQuery,
    (snapshot) => {
      const invoices: Invoice[] = [];

      snapshot.forEach((childSnapshot) => {
        const key = childSnapshot.key!;
        const data = childSnapshot.val();

        if (data.sent_status === "true_opened") {
          const servicesArr = Array.isArray(data.services) ? data.services : [];
          const computedTotal =
            servicesArr.length > 0
              ? calculateServicesTotal(servicesArr, false)
              : 0;
          const computedTotalWithFactor =
            servicesArr.length > 0
              ? calculateServicesTotal(servicesArr, true)
              : 0;
          // Build invoice ensuring header fields take precedence over any root-level duplicates
          const invoice: Invoice = {
            ...data, // bring raw data first
            key,
            id: data.id || key,
            project: data.header?.project || data.project || "",
            title: data.header?.title || data.title || "",
            pms: data.header?.pms || data.pms || "",
            os: data.header?.os || data.os || "",
            city: data.header?.city || data.city || "",
            date: data.header?.date || data.date || "",
            total: computedTotal,
            totalWithFactor: computedTotalWithFactor,
            status: getInvoiceStatus(data),
            checked: data.checked || false,
            viewedByFat: data.viewedByFat || false,
            paid: data.paid || false,
            attached: data.attached || data.archived || false,
            team: teamId,
            header: data.header || {},
            services: data.services || [],
            photos: data.photos || [],
            materials: data.materials || [],
          };

          invoices.push(invoice);
        }
      });

      callback(invoices.reverse());
    },
    (error) => {
      console.error("Error in invoice subscription:", error);
    },
  );

  // Return unsubscribe function
  return unsubscribe;
};

export const updateInvoice = async (
  teamId: string,
  invoiceKey: string,
  updates: Partial<Invoice>,
): Promise<void> => {
  const invoiceRef = ref(database, `forms/${teamId}/${invoiceKey}`);
  await update(invoiceRef, updates);
};

export const deleteInvoice = async (
  teamId: string,
  invoiceKey: string,
): Promise<void> => {
  const invoiceRef = ref(database, `forms/${teamId}/${invoiceKey}`);
  await remove(invoiceRef);
};

export const updateServicesPedido = async (updates: {
  [key: string]: any;
}): Promise<void> => {
  await update(ref(database), updates);
};

export const getServices = async (
  serviceType: "nde" | "ndm",
): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const servicesRef = ref(database, `defaults/services/${serviceType}`);

    onValue(
      servicesRef,
      (snapshot) => {
        const services: any[] = [];

        snapshot.forEach((childSnapshot) => {
          const serviceData = childSnapshot.val();
          services.push({
            id: childSnapshot.key,
            ...serviceData,
          });
        });

        resolve(services);
      },
      (error) => {
        reject(error);
      },
    );
  });
};

export const getMaterials = async (): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const materialsRef = ref(database, "defaults/materials/items");

    onValue(
      materialsRef,
      (snapshot) => {
        const materials: any[] = [];

        snapshot.forEach((childSnapshot) => {
          const materialData = childSnapshot.val();
          materials.push({
            id: childSnapshot.key,
            ...materialData,
          });
        });

        resolve(materials);
      },
      (error) => {
        reject(error);
      },
    );
  });
};

export const moveInvoice = async (
  teamId: string,
  invoiceKey: string,
  invoiceData: Invoice,
): Promise<void> => {
  const moveRef = ref(database, `moved/${teamId}/${invoiceKey}`);
  await update(moveRef, invoiceData);
  await deleteInvoice(teamId, invoiceKey);
};

export const createInvoice = async (
  teamId: string,
  invoiceData: Partial<Invoice>,
): Promise<string> => {
  const formsRef = ref(database, `forms/${teamId}`);
  const newInvoiceRef = push(formsRef);
  await update(newInvoiceRef, invoiceData);
  return newInvoiceRef.key!;
};

const dateToTimestamp = (date: string, time: string): number => {
  if (!date || !date.trim()) {
    // Return a default timestamp if date is empty
    return new Date().getTime();
  }

  const dateTime = `${date.trim()} ${time} GMT`;
  const timestamp = new Date(dateTime).getTime();

  // If the result is NaN, return a default timestamp
  if (isNaN(timestamp)) {
    console.warn(
      `Invalid date provided to dateToTimestamp: "${date}" "${time}"`,
    );
    return new Date().getTime();
  }

  return timestamp;
};

const getInvoiceStatus = (data: any): InvoiceStatus => {
  if (data.archived || data.attached) return "attached";
  if (data.paid) return "paid";
  if (data.viewedByFat) return "viewed";
  if (data.checked) return "checked";
  return "pending";
};

export const formatCurrency = (value: number): string => {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

export const formatDate = (dateValue: any): string => {
  if (!dateValue) return "";

  // Convert to string if it's not already
  const dateString = String(dateValue);

  // If it's already in a readable format, return as is
  if (dateString.includes("/") || dateString.includes("-")) {
    // If it's already in DD/MM/YYYY format, return as is
    if (
      dateString.includes("/") &&
      dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)
    ) {
      return dateString;
    }

    // Try to parse and format consistently
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
    }
    return dateString;
  }

  // If it's a timestamp (number or number as string)
  const timestamp =
    typeof dateValue === "number" ? dateValue : parseInt(dateString);
  if (!isNaN(timestamp) && timestamp > 0) {
    const date = new Date(timestamp);
    // Use UTC to avoid timezone shifts
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  return dateString;
};

export const checkServiceTypes = (services: any[]): boolean => {
  // Handle cases where services might not be an array
  if (!services || !Array.isArray(services) || services.length === 0)
    return true;

  // Normalize codes
  const codes = services
    .filter((s) => s && typeof s.code === "string" && s.code.trim() !== "")
    .map((s) => s.code.toUpperCase().trim());

  if (codes.length === 0) return true;

  // 1) Prefix rule: cannot mix NDE and NDM
  const hasNDE = codes.some((c) => c.startsWith("NDE"));
  const hasNDM = codes.some((c) => c.startsWith("NDM"));
  if (hasNDE && hasNDM) return false;

  // 2) Suffix rule: a boletim can only have codes ending in C, or II and RD.
  //    That means: it's invalid if any code ending in C appears together with
  //    any code ending in II or RD.
  const hasC = codes.some((c) => c.endsWith("C"));
  const hasII = codes.some((c) => c.endsWith("II"));
  const hasRD = codes.some((c) => c.endsWith("RD"));

  if (hasC && (hasII || hasRD)) return false;

  // Otherwise, considered valid
  return true;
};

// Helper function to check if invoice has mixed service types
export const hasMixedServiceTypes = (services: any[]): boolean => {
  return !checkServiceTypes(services);
};

// Split invoice into separate Investimento (I) and Custeio (C) invoices
export const splitInvoice = async (
  teamId: string,
  invoiceKey: string,
  originalInvoice: any,
): Promise<void> => {
  if (!originalInvoice.services || !Array.isArray(originalInvoice.services)) {
    throw new Error("Invoice has no valid services to split");
  }

  // Separate services by category (mutually exclusive assignment)
  // Precedence: Suffix over prefix.
  // - If ends with C -> Custeio (C)
  // - Else if ends with II or RD -> Investimento (I)
  // - Else if starts with NDE -> Investimento (I)
  // - Else if starts with NDM -> Custeio (C)
  const investimentoServices: any[] = [];
  const custeioServices: any[] = [];
  for (const service of originalInvoice.services) {
    const raw = service?.code;
    if (!raw || typeof raw !== "string") continue;
    const code = raw.toUpperCase().trim();

    if (code.endsWith("C")) {
      custeioServices.push(service);
      continue;
    }
    if (code.endsWith("II") || code.endsWith("RD")) {
      investimentoServices.push(service);
      continue;
    }
    if (code.startsWith("NDE")) {
      investimentoServices.push(service);
      continue;
    }
    if (code.startsWith("NDM")) {
      custeioServices.push(service);
      continue;
    }
    // If it doesn't match any known rule, leave it out of the split
  }

  if (investimentoServices.length === 0 && custeioServices.length === 0) {
    throw new Error("No services found for Investimento (I) or Custeio (C)");
  }

  // Helper to ensure factor is present in each service
  const ensureServiceFactor = (s: any) => ({
    ...s,
    factor:
      s && s.factor && parseFloat(String(s.factor)) > 0
        ? parseFloat(String(s.factor))
        : currentFactorCache,
  });

  // Fetch raw invoice to copy exact root fields (not UI-mapped),
  // especially the root `date` timestamp used for ordering
  let rawOriginal: any = null;
  try {
    const rawSnap = await get(ref(database, `forms/${teamId}/${invoiceKey}`));
    if (rawSnap.exists()) rawOriginal = rawSnap.val();
  } catch {}

  // Prefer the raw root timestamp; fall back to UI object if missing
  const originalRootDate =
    rawOriginal && typeof rawOriginal.date !== "undefined"
      ? rawOriginal.date
      : originalInvoice?.date;
  const originalSentStatus =
    rawOriginal && typeof rawOriginal.sent_status !== "undefined"
      ? rawOriginal.sent_status
      : originalInvoice?.sent_status || "true_opened";
  const originalTeam =
    rawOriginal && typeof rawOriginal.team !== "undefined"
      ? rawOriginal.team
      : originalInvoice?.team || teamId;

  // Create Investimento invoice (with 'I' suffix)
  if (investimentoServices.length > 0) {
    const iServices = investimentoServices.map(ensureServiceFactor);
    const iInvoice = {
      ...originalInvoice,
      id: originalInvoice.id + "I",
      date: originalRootDate,
      team: originalTeam,
      sent_status: originalSentStatus,
      services: iServices,
      total: calculateServicesTotal(iServices, false),
      totalWithFactor: calculateServicesTotal(iServices, true),
      created_at: Date.now(),
      created_by: "Split from original invoice",
    } as any;

    // Remove the key from the object to create a new one
    delete iInvoice.key;
    // Ensure header fields are not duplicated at root
    delete iInvoice.title;
    delete iInvoice.project;
    delete iInvoice.city;
    delete iInvoice.pms;
    delete iInvoice.os;

    // Use a push key to avoid invalid path characters and ensure visibility in lists
    const newIRef = push(ref(database, `forms/${teamId}`));
    await set(newIRef, iInvoice);
    try {
      console.log("[splitInvoice] Created Investimento (I) invoice", {
        teamId,
        newKey: newIRef.key,
        id: iInvoice.id,
        originalKey: invoiceKey,
      });
    } catch {}
  }

  // Create Custeio invoice (with 'C' suffix)
  if (custeioServices.length > 0) {
    const cServices = custeioServices.map(ensureServiceFactor);
    const cInvoice = {
      ...originalInvoice,
      id: originalInvoice.id + "C",
      date: originalRootDate,
      team: originalTeam,
      sent_status: originalSentStatus,
      services: cServices,
      total: calculateServicesTotal(cServices, false),
      totalWithFactor: calculateServicesTotal(cServices, true),
      created_at: Date.now(),
      created_by: "Split from original invoice",
    } as any;

    // Remove the key from the object to create a new one
    delete cInvoice.key;
    // Ensure header fields are not duplicated at root
    delete cInvoice.title;
    delete cInvoice.project;
    delete cInvoice.city;
    delete cInvoice.pms;
    delete cInvoice.os;

    // Use a push key to avoid invalid path characters and ensure visibility in lists
    const newCRef = push(ref(database, `forms/${teamId}`));
    await set(newCRef, cInvoice);
    try {
      console.log("[splitInvoice] Created Custeio (C) invoice", {
        teamId,
        newKey: newCRef.key,
        id: cInvoice.id,
        originalKey: invoiceKey,
      });
    } catch {}
  }

  // Remove the original invoice
  await remove(ref(database, `forms/${teamId}/${invoiceKey}`));
};

// Helper function to calculate services total
const calculateServicesTotal = (
  services: any[],
  withFactor: boolean,
): number => {
  return services.reduce((total: number, service: any) => {
    const price = parseFloat(service?.price?.toString?.() || "0");
    const quantity = parseFloat(service?.quantity?.toString?.() || "0");
    let factor = 1;
    if (withFactor) {
      const parsed = parseFloat(service?.factor?.toString?.() || "");
      factor = !isNaN(parsed) && parsed > 0 ? parsed : currentFactorCache;
    }
    return total + price * quantity * factor;
  }, 0);
};

export const getInvoicesForReport = async (
  teamId: string,
  startDate: string,
  endDate: string,
): Promise<Invoice[]> => {
  return new Promise((resolve, reject) => {
    const formsRef = ref(database, `forms/${teamId}`);
    const formsQuery = query(
      formsRef,
      orderByChild("date"),
      startAt(dateToTimestamp(startDate, "00:00")),
      endAt(dateToTimestamp(endDate, "23:59")),
    );

    onValue(
      formsQuery,
      (snapshot) => {
        const invoices: Invoice[] = [];

        snapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key!;
          const data = childSnapshot.val();

          // Check if invoice has proper sent status for reports
          const hasSentStatus =
            data.sent_status && data.sent_status.includes("true");
          const hasSentDate = data.sent_date && data.sent_date.includes("true");

          if (hasSentStatus || hasSentDate) {
            const servicesArr = Array.isArray(data.services)
              ? data.services
              : [];
            const computedTotal =
              servicesArr.length > 0
                ? calculateServicesTotal(servicesArr, false)
                : 0;
            const computedTotalWithFactor =
              servicesArr.length > 0
                ? calculateServicesTotal(servicesArr, true)
                : 0;
            // Build invoice ensuring header fields take precedence over any root-level duplicates
            const invoice: Invoice = {
              ...data,
              key,
              id: data.id || key,
              project: data.header?.project || data.project || "",
              title: data.header?.title || data.title || "",
              pms: data.header?.pms || data.pms || "",
              os: data.header?.os || data.os || "",
              city: data.header?.city || data.city || "",
              date: data.header?.date || data.date || "",
              total: computedTotal,
              totalWithFactor: computedTotalWithFactor,
              status: getInvoiceStatus(data),
              checked: data.checked || false,
              viewedByFat: data.viewedByFat || false,
              paid: data.paid || false,
              attached: data.attached || false,
              team: teamId,
              header: data.header || {},
              services: data.services || [],
              photos: data.photos || [],
              materials: data.materials || [],
            };

            invoices.push(invoice);
          }
        });

        resolve(invoices.reverse());
      },
      (error) => {
        reject(error);
      },
    );
  });
};

// User management functions

export const getAllUsers = async (): Promise<User[]> => {
  return new Promise((resolve, reject) => {
    // Try users-mobile first (main user storage)
    const mobileUsersRef = ref(database, "users-mobile");

    onValue(
      mobileUsersRef,
      (snapshot) => {
        const users: User[] = [];

        if (snapshot.exists()) {
          const usersData = snapshot.val();
          Object.keys(usersData).forEach((userId) => {
            const userData = usersData[userId];
            users.push({
              uid: userId,
              email: userData.email || "",
              name: userData.name || "",
              type: userData.type || "team",
              team: userData.team || "",
              supervision: userData.supervision
                ? userData.supervision.split(",").map((t: string) => t.trim())
                : [],
            });
          });
        }

        // Also check users table for any additional admin/supervisor accounts
        const usersRef = ref(database, "users");
        onValue(
          usersRef,
          (usersSnapshot) => {
            if (usersSnapshot.exists()) {
              const additionalUsersData = usersSnapshot.val();
              Object.keys(additionalUsersData).forEach((userId) => {
                // Only add if not already in the array
                if (!users.find((u) => u.uid === userId)) {
                  const userData = additionalUsersData[userId];
                  users.push({
                    uid: userId,
                    email: userData.email || "",
                    name: userData.name || "",
                    type: userData.type || "team",
                    team: userData.team || "",
                    supervision: userData.supervision
                      ? userData.supervision
                          .split(",")
                          .map((t: string) => t.trim())
                      : [],
                  });
                }
              });
            }

            resolve(users);
          },
          (error) => {
            // If users table fails, still return mobile users
            resolve(users);
          },
        );
      },
      (error) => {
        reject(error);
      },
    );
  });
};

export const updateUserSupervision = async (
  userId: string,
  supervision: string[],
): Promise<void> => {
  // Try to update in users-mobile first
  const mobileUserRef = ref(database, `users-mobile/${userId}`);

  try {
    await update(mobileUserRef, {
      supervision: supervision.join(","),
    });
  } catch (error) {
    // If users-mobile fails, try users table
    try {
      const userRef = ref(database, `users/${userId}`);
      await update(userRef, {
        supervision: supervision.join(","),
      });
    } catch (fallbackError) {
      console.error(
        "Error updating user supervision in both locations:",
        error,
        fallbackError,
      );
      throw error;
    }
  }
};

export const getSupervisors = async (): Promise<User[]> => {
  const allUsers = await getAllUsers();
  return allUsers.filter((user) => user.type === "superv");
};

export const getAvailableTeams = (): string[] => {
  return [
    "ORCDISTBA01",
    "ORCDISTBA02",
    "ORCDISTBA03",
    "ORCDISTBA04",
    "ORCDISTBA05",
    "ORCDISTBA06",
    "ORCDISTBA07",
    "ORCDISTBA08",
    "ORCDISTBA09",
    "ORCDISTBA10",
    "ORCDISTBA11",
    "ORCDISTBA12",
    "ORCDISTBA13",
    "ORCDISTBA14",
    "ORCDISTBA15",
    "ORCDISTBA16",
  ];
};
